/*
  Grid.jsx

  This is meant to replicate the MUI Grid system:
  https://mui.com/material-ui/react-grid/

  TODO: Change "botech-container" and "botech-item" to generated classnames.
*/

// Npm.
import React, { useMemo } from 'react';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => {

  // Get container styles.
  // We just support up to a spacing of 8 which should be enough.

  const container = {
    display: 'flex',
    flexFlow: 'row wrap',
    '&.align-flex-start': {
      alignItems: 'flex-start',
    },
    '&.align-center': {
      alignItems: 'center',
    },
    '&.align-flex-end': {
      alignItems: 'flex-end',
    },
  };

  for (let i = 1; i <= 8; i++) {
    container[`&.spacing-${i}`] = {
      marginTop: theme.space(-i),
      marginLeft: theme.space(-i),
      width: `calc(100% + ${theme.space(i)})`,
      '& > .botech-item': {
        paddingTop: theme.space(i),
        paddingLeft: theme.space(i),
      }
    };
  }

  // Get item styles.

  const item = {
    margin: '0px',
    flexDirection: 'row',
    flexBasis: '100%',
    flexGrow: '0',
    maxWidth: '100%',
  };

  const itemSm = {};
  const itemMd = {};
  const itemLg = {};

  const inv = 1 / 12;

  for (let i = 1; i <= 12; i++) {
    
    const value = `${(i * inv * 100).toFixed(4)}%`.replace('.0000%', '%');
    const rules = {
      flexBasis: value,
      maxWidth: value,
    };

    item[`&.xs-${i}`] = rules;
    itemSm[`&.sm-${i}`] = rules;
    itemMd[`&.md-${i}`] = rules;
    itemLg[`&.lg-${i}`] = rules;

  }

  item[theme.breakpointUp('xs')] = itemSm;
  item[theme.breakpointUp('sm')] = itemMd;
  item[theme.breakpointUp('md')] = itemLg;

  return {
    root: {
      boxSizing: 'border-box',
      '&.botech-container': container,
      '&.botech-item': item,
    }
  };

});

/**
 * Helper to get the correct className to use.
 */
function getGridClassName(container, spacing, align, xs, sm, md, lg) {
  if (container) {

    let className = `Grid botech-container spacing-${spacing}`;

    if (
      align === 'flex-start'
      || align === 'center'
      || align === 'flex-end'
    ) {
      className += ` align-${align}`;
    }

    return className;

  } else {

    xs = xs || 12;
    sm = sm || xs;
    md = md || sm;
    lg = lg || md;

    return `Grid botech-item xs-${xs} sm-${sm} md-${md} lg-${lg}`;

  }
}

/**
 * Icon button component.
 */
export default function Grid({
  className,
  container = false,
  spacing = 0,
  align,
  xs = 0,
  sm = 0,
  md = 0,
  lg = 0,
  ...rest
}) {

  const classes = useStyles();
  const gridClass = getGridClassName(container, spacing, align, xs, sm, md, lg);
  const cls = useMemo(() => cx(gridClass, classes.root, className), [gridClass, classes.root, className]);

  return (
    <div className={cls} {...rest} />
  );
}
